import { useState } from "react";
import { Alert, Badge, Button, Card, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ReactComponent as KSeFLogo } from "../../../../../../../../../../../../images/svg/invoices/KSeF_logo.svg";
import { ReactComponent as VerticalDots } from "../../../../../../../../../../../../images/svg/menu/dots.svg";
import { apiOrganization } from "../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import { KSeFIntegrationCardProps } from "../../utils/types";

export const KSeFIntegrationCard = ({
	setShowIntegrationWithKSeFModal,
	form,
	onSubmit,
	KSeFInstalled,
	KSeFTestInvoiceCount,
	KSeFEnvironment,
	onFetchInvoiceDetails,
}: KSeFIntegrationCardProps) => {
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const { watch, setValue, clearErrors } = form;
	const isTestEnvironment = watch("ksef_environment_type") === "TEST";
	const confirmation = useConfirmation();
	const { isSmallBreakpoint } = useWindowSize();
	const { addFlash, addSuccessFlash } = useFlash();

	const removeKSeFIntegrationHandler = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.remove"),
			});

			setValue("ksef_token", undefined);
			setValue("send_to_ksef_automatically", false);
			setValue("ksef_environment_type", "TEST");
			setValue("should_delete_ksef_auth_cert", true);
			setValue("should_delete_ksef_offline_cert", true);
			clearErrors([
				"ksef_crt_offline_cert_file",
				"ksef_key_offline_cert_file",
				"ksef_crt_auth_cert_file",
				"ksef_key_auth_cert_file",
			]);

			await onSubmit();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const removeKSeFTestInvoicesHandler = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.remove"),
			});
			setLoading(true);
			await apiOrganization.removeTestKSeFInvoices();
			await onFetchInvoiceDetails();
			addSuccessFlash(t("lib:common.flash.removed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Card data-testid="ksef-integration-card">
			<div className="d-flex flex-column flex-sm-row align-items-center p-3 row-gap-3 column-gap-sm-3">
				<div className="p-2 border rounded">
					<KSeFLogo className="flex-shrink-0" />
				</div>
				<div className="d-flex flex-column row-gap-1">
					<div className="d-flex column-gap-2">
						<p className="mb-0 font-weight-bold flex-shrink-0">
							{t("modules.invoice.field.ksef_integration.title")}
						</p>
						{isTestEnvironment && KSeFInstalled && (
							<Badge bg="warning" text="dark">
								{t("modules.invoice.field.test_environment.title")}
							</Badge>
						)}
					</div>
				</div>
				{KSeFInstalled ? (
					<div className="ms-auto d-flex align-self-start align-self-sm-center align-items-center column-gap-2">
						<Button variant="light" onClick={() => setShowIntegrationWithKSeFModal(true)}>
							{t("lib:common.action.edit")}
						</Button>
						{isSmallBreakpoint ? (
							<Button variant="danger" onClick={removeKSeFIntegrationHandler}>
								{t("lib:common.action.remove")}
							</Button>
						) : (
							<Dropdown className="ksef-edit-more-actions-dropdown">
								<Dropdown.Toggle variant="light" as={VerticalDots} />
								<Dropdown.Menu>
									<Dropdown.Item className="text-danger" onClick={removeKSeFIntegrationHandler}>
										{t("lib:common.action.remove")}
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						)}
					</div>
				) : (
					<Button
						className="align-self-start align-self-sm-center ms-sm-auto"
						onClick={() => setShowIntegrationWithKSeFModal(true)}
					>
						{t("lib:common.action.install")}
					</Button>
				)}
			</div>
			{KSeFTestInvoiceCount > 0 && KSeFEnvironment === "PROD" && (
				<Alert
					className="mx-3 d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 row-gap-sm-0"
					variant="warning"
					data-testid="ksef-test-invoices-alert"
				>
					<p className="mb-0 align-self-start align-self-sm-center">
						{t("modules.invoice.field.ksef_invoices_in_test_mode.title", {
							invoicesCount: KSeFTestInvoiceCount,
						})}
					</p>
					<ButtonLoading
						loading={loading}
						onClick={removeKSeFTestInvoicesHandler}
						className="align-self-start align-self-sm-center"
					>
						{t("lib:common.action.remove")}
					</ButtonLoading>
				</Alert>
			)}
		</Card>
	);
};
